/// <amd-module name="WorkItemsHub/Stores/WorkItemsHubStore" />
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Action } from "VSS/Reflux/Action";
import { Store } from "VSS/Reflux/Store";
import { ActionsHub } from "WorkItemsHub/Actions/ActionsHub";
import { FeatureFlags } from "WorkItemsHub/Constants";
import {
    IWorkItemsHubFilterUpdateData,
    IWorkItemsHubSortItemsData,
    IWorkItemsHubShowCompletedItemsData,
    IWorkItemsHubRemoveWorkItemData,
    IWorkItemsHubPagedWorkItemData
} from "WorkItemsHub/DataContracts/IActionsDataContract";
import { WorkItemsHubTabs } from "WorkItemsHub/Generated/Constants";
import { WorkItemsHubData, WorkItemsHubPermissionsData } from "WorkItemsHub/Generated/Contracts";
import { IWorkItemsHubFilterDataSource, PageLoadingState } from "WorkItemsHub/Stores/IWorkItemsHubFilterDataSource";
import { WorkItemsHubFilterDataSource } from "WorkItemsHub/Stores/WorkItemsHubFilterDataSource";
import * as WorkItemsHubTabUtils from "WorkItemsHub/Utils/WorkItemsHubTabUtils";
import { AssignedToFilterValueProvider, StateFilterValueProvider, WorkItemTypeFilterValueProvider } from "Wit/Filter/FilterValueProviders";
import { IWorkItemFilterField, WorkItemFilterFieldType, DataSourceFilterValueProvider } from "Wit/Filter/WorkItemFilter";
import { IWorkItemsHubUpdateData } from "DataContracts/IActionsDataContract";
import { AssignedToFilterProvider } from "Wit/Filter/AssignedToFilterProvider";
import { FieldFilterProvider } from "Wit/Filter/FieldFilterProvider";
import { AreaPathFilterProvider } from "Wit/Filter/AreaPathFilterProvider";
import { FilterManager } from "Wit/Filter/FilterManager";
import { TagsFilterProvider } from "Wit/Filter/TagsFilterProvider";
import { SubstringTextFilterProvider, TextFilterProvider, TextFilterProviderWithUnassigned } from "Wit/Filter/TextFilterProvider";
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import * as Resources from "WorkItemsHub/Resources";

interface IActionListenerPairs {
    action: Action<{}>;
    listener: (param: {}) => void;
}

export class WorkItemsHubStore extends Store {
    private _actionListenerPairs: IActionListenerPairs[] = [];
    private _filterManager: { [key: string]: FilterManager } = {};
    private _dataSource: { [key: string]: IWorkItemsHubFilterDataSource } = {};
    private _permission: WorkItemsHubPermissionsData;
    private _project: IProjectData;
    private _pageContext: IVssPageContext;

    constructor(
        actionsHub: ActionsHub,
        pageContext: IVssPageContext,
        initialTabId: string,
        initialData?: WorkItemsHubData, // Not providing initialData is allowed, but will set the store to Error state
        additionalHubData?: WorkItemsHubData[], // only for unit tests
        showCompletedItemsOverride: boolean = null
    ) {
        super("WorkItemsHubStoreChangedEvent");

        this._pageContext = pageContext;

        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        this._project = tfsPageData.project;

        if (initialData) {
            this._setPermissions(initialData.permission);
        }

        this._initializeData(initialTabId, initialData, showCompletedItemsOverride);
        if (additionalHubData) {
            additionalHubData.forEach((data: WorkItemsHubData) => {
                if (data) {
                    this._initializeData(data.processSettings.tabId, data);
                }
            });
        }

        this._addListenerToActionsHub(actionsHub.invalidateAllTabsData, this._invalidateAllTabsData);
        this._addListenerToActionsHub(actionsHub.invalidateTabData, this._invalidateTabData);
        this._addListenerToActionsHub(actionsHub.removeWorkItems, this._removeWorkItems);
        this._addListenerToActionsHub(actionsHub.endRefreshDataProvider, this._endRefreshDataProvider);
        this._addListenerToActionsHub(actionsHub.updateFilter, this._onUpdateFilter);
        this._addListenerToActionsHub(actionsHub.setCompletedItemsVisibility, this._setCompletedItemsVisibility);
        this._addListenerToActionsHub(actionsHub.sortItems, this._sortItems);
        this._addListenerToActionsHub(actionsHub.endPageWorkItems, this._addWorkItems);
    }

    /**
     * Returns data source for a specified tab.
     * @param tabId
     */
    public getHubFilterDataSource(tabId: string): IWorkItemsHubFilterDataSource {
        return this._dataSource[tabId];
    }

    /**
     * Returns visible data for a specified tab.
     * @param tabId
     */
    public getHubDisplayData(tabId: string): WorkItemsHubData {
        return this.isHubDataInitialized(tabId) ? this._dataSource[tabId].getFilteredData() : null;
    }

    /**
     * Returns count of visible items for a specified tab.
     * @param tabId
     */
    public getVisibleItemsCount(tabId: string): number {
        return this.isHubDataInitialized(tabId) ? this._dataSource[tabId].getVisibleItemsCount() : 0;
    }

    /**
     * Returns count of total items for a specified tab.
     * @param tabId
     */
    public getTotalItemsCount(tabId: string): number {
        return this.isHubDataInitialized(tabId) ? this._dataSource[tabId].getItemCount() : 0;
    }

    /**
     * Returns true if the data for a specified tab has been initialized. Otherwise return false.
     * @param tabId
     */
    public isHubDataInitialized(tabId: string): boolean {
        return !!this._dataSource[tabId];
    }

    /**
     * Returns true if there was an unexpected error retrieving the data for a specified tab. Otherwise return false.
     * @param tabId
     */
    public isHubDataError(tabId: string): boolean {
        return this._dataSource[tabId] === null;
    }

    /**
     * Returns true if the specified tab is supported for the current user
     * If false, it means the given tab is not supported.
     * For instance, my teams view is not supproted if user is not a member of any team.
     * Following page is not availble if email is not configured.
     * @param tabId
     */
    public isSupportedFeature(tabId: string): boolean {
        if (!this.isHubDataInitialized(tabId)) {
            return true;
        }

        const dataSource = this._dataSource[tabId];
        return dataSource.isFeatureSupported();
    }

    /**
     * Returns the work items hub permission for the current user
     * @returns work items hub permissions data
     */
    public getWorkItemsHubPermission(): WorkItemsHubPermissionsData {
        return this._permission;
    }

    public getFilterManager(tabId: string): FilterManager {
        return this._filterManager[tabId];
    }

    /**
     * Returns the filter fields for a specified tab.
     * @param tabId
     */
    public getHubFilterFields(tabId: string): IWorkItemFilterField[] {
        const dataSource = this._dataSource[tabId];
        const projectName = this._project.name;

        const filterFields: IWorkItemFilterField[] = [
            {
                displayType: WorkItemFilterFieldType.Text,
                fieldName: "text",
                placeholder: Resources.FilterByKeyword
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.WorkItemType,
                placeholder: Resources.FilterByTypes,
                noItemsText: Resources.FilterNoTypes,
                valueProvider: new WorkItemTypeFilterValueProvider(projectName, dataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.State,
                placeholder: Resources.FilterByStates,
                noItemsText: Resources.FilterNoStates,
                valueProvider: new StateFilterValueProvider(projectName, dataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Tags,
                placeholder: Resources.FilterByTags,
                noItemsText: Resources.FilterNoTags,
                showOrAndOperators: true
            }
        ];

        if (dataSource.hasAreaPath()) {
            const areaPathFilter: IWorkItemFilterField[] = [
                {
                    displayType: WorkItemFilterFieldType.CheckboxList,
                    fieldName: CoreFieldRefNames.AreaPath,
                    placeholder: Resources.FilterByAreaPath,
                    noItemsText: Resources.FilterNoArea,
                    valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.AreaPath, dataSource)
                }
            ];
            filterFields.splice(3, 0, areaPathFilter[0]);
        }

        if (localeIgnoreCaseComparer(tabId, WorkItemsHubTabs[WorkItemsHubTabs.AssignedToMe]) !== 0) {
            const excludeMeFilter = !this._permission.personalView.hasPermission;
            const assignedToFilter: IWorkItemFilterField[] = [
                {
                    displayType: WorkItemFilterFieldType.CheckboxList,
                    fieldName: CoreFieldRefNames.AssignedTo,
                    placeholder: Resources.FilterByAssignedTo,
                    valueProvider: new AssignedToFilterValueProvider(this._pageContext, dataSource, excludeMeFilter)
                }
            ];
            filterFields.splice(2, 0, assignedToFilter[0]);
        }

        return filterFields;
    }

    /**
     * Disposes the store.
     */
    public dispose(): void {
        if (this._actionListenerPairs) {
            this._actionListenerPairs.forEach(pair => pair.action.removeListener(pair.listener));
            this._actionListenerPairs = null;
        }
    }

    private _setPermissions(permissionsData: WorkItemsHubPermissionsData): void {
        this._permission = {
            personalView: { hasPermission: false },
            query: { hasPermission: false },
            sendEmail: { hasPermission: false },
            newWorkItem: { hasPermission: false }
        };

        if (permissionsData) {
            this._permission.personalView.hasPermission = permissionsData.personalView ? permissionsData.personalView.hasPermission : false;
            this._permission.query.hasPermission = permissionsData.query ? permissionsData.query.hasPermission : false;
            this._permission.sendEmail.hasPermission = permissionsData.sendEmail ? permissionsData.sendEmail.hasPermission : false;
            this._permission.newWorkItem.hasPermission = permissionsData.newWorkItem ? permissionsData.newWorkItem.hasPermission : false;
        }
    }

    private _invalidateTabData = (tabId: string): void => {
        this._dataSource[tabId] = undefined;
        this.emitChanged();
    };

    private _removeWorkItems = (data: IWorkItemsHubRemoveWorkItemData): void => {
        if (!data) {
            return;
        }

        let hasChanged: boolean = false;
        for (const key in this._dataSource) {
            const currentTabDataSource = this._dataSource[key];
            if (!currentTabDataSource) {
                continue;
            }

            if (key === data.tabId) {
                hasChanged = currentTabDataSource.removeWorkItems(data.workItemIds);
            } else {
                this._dataSource[key] = undefined;
                hasChanged = true;
            }
        }

        if (hasChanged) {
            // Update the filter manager only if any of the work items have been removed or a tab's data has been invalidated
            const filterManager = this._filterManager[data.tabId];
            if (filterManager) {
                filterManager.dataUpdated();
            }
            this.emitChanged();
        }
    };

    private _addWorkItems = (data: IWorkItemsHubPagedWorkItemData): void => {
        const tabData = this._dataSource[data.tabId];

        if (data) {
            const hasChanged = tabData.addWorkItems(data);
            if (hasChanged) {
                const filterManager = this._filterManager[data.tabId];
                if (filterManager) {
                    filterManager.dataUpdated();
                    this._updateDisplayData(data.tabId);
                }
            }
        }

        this.emitChanged();
    };

    private _invalidateAllTabsData = (): void => {
        this._dataSource = {};
        this.emitChanged();
    };

    private _endRefreshDataProvider = (hubUpdateData: IWorkItemsHubUpdateData): void => {
        this._initializeData(hubUpdateData.tabId, hubUpdateData.tabData);
        this.emitChanged();
    };

    private _setCompletedItemsVisibility = (payload: IWorkItemsHubShowCompletedItemsData) => {
        const { showCompletedItems, tabId } = payload;

        if (tabId !== WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.RecentlyCompleted]) {
            this._setViewOptions(tabId, dataSource => dataSource.setCompletedItemsVisibility(showCompletedItems));
        }
        this.emitChanged();
    };

    private _sortItems = (payload: IWorkItemsHubSortItemsData) => {
        const { tabId, sortOptions } = payload;
        this._setViewOptions(tabId, dataSource => dataSource.setSortInfo(sortOptions));
        this.emitChanged();
    };

    private _setViewOptions(tabId: string, func: (param: IWorkItemsHubFilterDataSource) => void) {
        const dataSource = this._dataSource[tabId];
        func(dataSource);

        // Filter values need to be recalculated (by invalidating cache) and
        // mark index is dirty (so that next search will trigger rebuilding index)
        const filterManager = this._filterManager[tabId];
        filterManager.dataUpdated();

        // Update visible ids for each tab
        this._updateDisplayData(tabId);
    }

    private _addListenerToActionsHub(action: Action<{}>, listener: (param: {}) => void): void {
        this._actionListenerPairs.push({ action: action, listener: listener });
        action.addListener(listener);
    }

    private _initializeData(tabId: string, data: WorkItemsHubData, showCompletedItemsOverride: boolean = null) {
        if (!data) {
            // _initializeData must be called with actual data object.
            // If it is called with null/undefined - it means error loading data.
            this._dataSource[tabId] = null;
            return;
        }

        if (this._dataSource[tabId]) {
            console.warn("Should not reinitialized the data for same tab");
            return;
        }

        if (tabId !== data.processSettings.tabId) {
            console.warn("Provided tabId must match the tab data");
            return;
        }

        let showCompletedItems = true;
        if (showCompletedItemsOverride == null) {
            showCompletedItems = data.userSettings && data.userSettings.showCompleted;
        } else {
            showCompletedItems = showCompletedItemsOverride;
        }

        const dataSource = new WorkItemsHubFilterDataSource(this._pageContext, data, this._project.id, showCompletedItems);
        this._dataSource[tabId] = dataSource;

        //get pageData so we can pass in the current user identityref to the assignedtofilterprovider
        const pageService = this._pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        const filterManager = new FilterManager(dataSource);
        this._filterManager[tabId] = filterManager;
        filterManager.clearFilterProviders();
        if (isFeatureFlagEnabled(this._pageContext, FeatureFlags.UseTokenFiltering, false)) {
            filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new TextFilterProviderWithUnassigned());
        } else {
            filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new SubstringTextFilterProvider());
        }
        filterManager.registerFilterProvider(CoreFieldRefNames.WorkItemType, new FieldFilterProvider(CoreFieldRefNames.WorkItemType));
        filterManager.registerFilterProvider(CoreFieldRefNames.State, new FieldFilterProvider(CoreFieldRefNames.State));
        filterManager.registerFilterProvider(CoreFieldRefNames.AssignedTo, new AssignedToFilterProvider(pageData.user, this._pageContext));
        filterManager.registerFilterProvider(CoreFieldRefNames.AreaPath, new AreaPathFilterProvider());
        filterManager.registerFilterProvider(CoreFieldRefNames.Tags, new TagsFilterProvider());
        filterManager.activate(this._pageContext);

        const updatedFilter = dataSource.getFilterState();
        this._onUpdateFilterInternal({ tabId, updatedFilter }, true);
    }

    private _onUpdateFilter = (payload: IWorkItemsHubFilterUpdateData) => {
        const isUpdated = this._onUpdateFilterInternal(payload);
        if (isUpdated) {
            this.emitChanged();
        }
    };

    private _onUpdateFilterInternal(payload: IWorkItemsHubFilterUpdateData, isInitializing: boolean = false): boolean {
        const { tabId, updatedFilter } = payload;

        const filterManager = this._filterManager[tabId];
        if (filterManager) {
            const filters = updatedFilter;
            if (isInitializing) {
                // don't unnecessary save the filters when initializing the store/switching tab (we're loading the default/saved user settings from data provider)
                this._dataSource[tabId].setFilters(filters);
            } else {
                this._dataSource[tabId].saveFilters(filters);
            }
            filterManager.setFilters(filters);
            this._updateDisplayData(tabId, isInitializing);
            return true;
        }

        return false;
    }

    private _updateDisplayData(tabId: string, isInitializing: boolean = false): void {
        const dataSource = this._dataSource[tabId];
        const filterManager = this._filterManager[tabId];
        if (filterManager) {
            const isFiltering = filterManager.isFiltering();
            if (isFiltering) {
                const ids = filterManager.filter(this._pageContext);
                dataSource.setVisibleIds(ids);
            } else {
                const save = !isInitializing;
                dataSource.resetFilter(save);
            }
        }

        if (tabId !== WorkItemsHubTabUtils.TabIdByTabEnumValueMap[WorkItemsHubTabs.RecentlyCompleted] && !dataSource.shouldShowCompletedItems()) {
            dataSource.hideCompletedItems();
        }
    }
}
